import { ICatalogue, ICatalogueCustomization } from "../../apis/useGetCatalogueData/types";

// Compact share-cart wire format (v2): "<v>*<item>*<item>..." where each item is
// "<bmi>.<qty>.<opts>.<sets>" and opts/sets are "-"-joined id lists (empty when
// none). Only digits and . * - appear, all within the QR alphanumeric-mode
// charset, so the QR is far smaller than the previous base64(JSON) form. Bumping
// the version invalidates QRs from an older build; decodeCart rejects mismatched
// versions gracefully (returns null -> "invalid QR").
const VERSION = 2;

const parseIds = (segment: string | undefined): number[] =>
	segment ? segment.split("-").map(Number) : [];

const selectedOptions = (
	customizations: ICatalogueCustomization[] | null | undefined
): number[] => {
	if (!customizations) return [];
	const ids: number[] = [];

	for (const c of customizations) {
		for (const opt of c.food_supplier_customization_options ?? []) {
			if (opt.quantity > 0) ids.push(opt.brand_customization_option_id);
		}
	}

	return ids;
};

const selectedSets = (sets: ICatalogue["food_supplier_set_items"]): number[] => {
	if (!sets) return [];

	return sets.filter((s) => s.quantity > 0).map((s) => s.food_supplier_menu_item_id);
};

export const encodeCart = (cart: ICatalogue[]): string => {
	const items = cart.map((item) => {
		const opts = selectedOptions(item.food_supplier_customizations).join("-");
		const sets = selectedSets(item.food_supplier_set_items).join("-");

		return `${item.brand_menu_item_id}.${item.quantity}.${opts}.${sets}`;
	});

	return `${VERSION}*${items.join("*")}`;
};

export const decodeCart = (encoded: string, catalogue: ICatalogue[]): ICatalogue[] | null => {
	try {
		const [versionSegment, ...itemSegments] = encoded.split("*");

		if (Number(versionSegment) !== VERSION) return null;

		const result: ICatalogue[] = [];

		for (const segment of itemSegments) {
			if (!segment) continue;

			const [bmiSegment, qtySegment, optsSegment, setsSegment] = segment.split(".");
			const bmi = Number(bmiSegment);
			const qty = Number(qtySegment);

			if (!Number.isInteger(bmi) || !Number.isInteger(qty)) continue;

			const opts = parseIds(optsSegment);
			const sets = parseIds(setsSegment);

			const base = catalogue.find((c) => c.brand_menu_item_id === bmi);

			if (!base) continue;

			const item: ICatalogue = {
				...base,
				quantity: qty,
				food_supplier_customizations:
					base.food_supplier_customizations?.map((c) => ({
						...c,
						food_supplier_customization_options:
							c.food_supplier_customization_options
								?.filter((opt) => opts.includes(opt.brand_customization_option_id))
								.map((opt) => ({ ...opt, quantity: 1 })) ?? null,
					})) ?? null,
				food_supplier_set_items:
					base.food_supplier_set_items
						?.filter((s) => sets.includes(s.food_supplier_menu_item_id))
						.map((s) => ({ ...s, quantity: 1 })) ?? null,
			};

			result.push(item);
		}

		return result.length > 0 ? result : null;
	} catch {
		return null;
	}
};
